import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Building2, LayoutDashboard, Search } from "lucide-react";
import { OnboardingSuccessDialog } from "@/components/certs/OnboardingSuccessDialog";
import { successFromFranchise } from "@/lib/certs";
import { api } from "@/lib/api";
import { inr, inrCompact, num } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function FranchisePartners() {
  const { can } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [done, setDone] = useState(null);
  const nav = useNavigate();

  const load = () => api.get(`/franchise${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(({ data }) => setList(data));
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const save = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    try { const { data } = await api.post("/franchise", f); toast.success("Franchise partner added"); setOpen(false); setF({}); load(); setDone(successFromFranchise(data)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Franchise Partners" subtitle={`${list.length} partners`}>
        <Button variant="outline" onClick={() => nav("/franchise-dashboard")} data-testid="franchise-dashboard-btn"><LayoutDashboard className="mr-1 h-4 w-4" /> Dashboard</Button>
        {can("fr.add") && <Button onClick={() => setOpen(true)} data-testid="add-franchise-btn"><Plus className="mr-1 h-4 w-4" /> Add Franchise Partner</Button>}
      </PageHeader>

      <div className="mb-4 relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search name / referral / contact / city…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="franchise-search" />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Partner</TableHead><TableHead>Referral</TableHead><TableHead>Location</TableHead>
            <TableHead className="text-right">DPs</TableHead><TableHead className="text-right">Orders</TableHead>
            <TableHead className="text-right">Earnings</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((fr) => (
              <TableRow key={fr.id} className="cursor-pointer" onClick={() => nav(`/franchise/${fr.id}`)} data-testid={`franchise-row-${fr.referral_number}`}>
                <TableCell><div className="flex items-center gap-3"><div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/15 text-violet-500"><Building2 className="h-4 w-4" /></div><div><p className="font-semibold">{fr.name}</p><p className="text-xs text-muted-foreground">{fr.contact} · {fr.email}</p></div></div></TableCell>
                <TableCell className="font-mono text-xs">{fr.referral_number}</TableCell>
                <TableCell>{fr.city}{fr.state ? `, ${fr.state}` : ""}</TableCell>
                <TableCell className="text-right tabular">{num(fr.stats?.total_dp)} <span className="text-emerald-500">({fr.stats?.active_dp})</span></TableCell>
                <TableCell className="text-right tabular">{num(fr.stats?.total_orders)}</TableCell>
                <TableCell className="text-right tabular font-semibold text-emerald-500">{inr(fr.stats?.total_earning)}</TableCell>
                <TableCell><StatusBadge status={fr.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-card" data-testid="franchise-dialog">
          <DialogHeader><DialogTitle className="font-head">Add Franchise Partner</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Franchise Partner Name *</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="franchise-name" /></div>
            <div><Label>Referral Number</Label><Input value={f.referral_number || ""} onChange={(e) => setF({ ...f, referral_number: e.target.value })} placeholder="auto e.g. FRN006" /></div>
            <div><Label>Contact Person</Label><Input value={f.contact_person || ""} onChange={(e) => setF({ ...f, contact_person: e.target.value })} data-testid="franchise-contact-person" /></div>
            <div><Label>Contact Number</Label><Input value={f.contact || ""} onChange={(e) => setF({ ...f, contact: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div><Label>PAN (for TDS)</Label><Input value={f.pan || ""} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" maxLength={10} data-testid="franchise-pan-input" /></div>
            <div><Label>GSTIN (optional)</Label><Input value={f.gstin || ""} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })} maxLength={15} data-testid="franchise-gstin-input" /></div>
            <div><Label>City</Label><Input value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
            <div><Label>State</Label><Input value={f.state || ""} onChange={(e) => setF({ ...f, state: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
            <div className="col-span-2"><Label>Remarks</Label><Input value={f.remarks || ""} onChange={(e) => setF({ ...f, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-franchise">Add Partner</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <OnboardingSuccessDialog partner={done} onClose={() => setDone(null)} />
    </div>
  );
}
